/**
 * Detects a "double tap" of a single modifier key (Shift, Alt / Option, or
 * Ctrl / Cmd) — two quick, standalone press-and-release cycles in succession,
 * with nothing else pressed in between. Used to LOCK the matching snap mode
 * (issue #390; Ctrl / Cmd → Angle in #927): instead of the modifiers only
 * being temporary while-held keys, tapping one twice toggles the
 * corresponding persistent snap toggle.
 *
 * Kept as a pure, time-injected state machine so the timing logic is unit
 * testable without faking real keyboard events / clocks. `app.ts` feeds it
 * the raw `keydown` / `keyup` stream and a `performance.now()` timestamp.
 *
 * What counts as a tap:
 *   - The modifier goes down (a fresh press, not OS auto-repeat) and back up
 *     within `tapMaxMs`.
 *   - No other key was pressed during that hold (so chords like Cmd+Shift+G
 *     never register as a Shift tap).
 *
 * A double tap fires when two clean taps complete within `gapMaxMs` of each
 * other. Any intervening other-key press, a long hold, or too large a gap
 * resets the sequence, so an accidental tap doesn't "arm" a stray later tap.
 */

export type ModifierKeyName = "Shift" | "Alt" | "Control" | "Meta";

export interface KeyEventLike {
  /** `KeyboardEvent.key` — "Shift", "Alt", a letter, etc. */
  key: string;
  /** `KeyboardEvent.repeat` — true for OS auto-repeat keydowns. */
  repeat?: boolean;
}

export class ModifierTapDetector {
  private readonly tapMaxMs: number;
  private readonly gapMaxMs: number;
  /** Timestamp the current hold began, or null when the key is up. */
  private downAt: number | null = null;
  /** Another key was pressed during the current hold → not a clean tap. */
  private polluted = false;
  /** End time of the previous clean solo tap, used to measure the gap. */
  private lastTapEnd = Number.NEGATIVE_INFINITY;

  constructor(
    private readonly key: ModifierKeyName,
    opts: { tapMaxMs?: number; gapMaxMs?: number } = {},
  ) {
    this.tapMaxMs = opts.tapMaxMs ?? 300;
    this.gapMaxMs = opts.gapMaxMs ?? 400;
  }

  /** Feed every keydown. Never completes a double tap on its own. */
  onKeyDown(e: KeyEventLike, now: number): void {
    if (e.key === this.key) {
      if (e.repeat) return; // OS auto-repeat while held — ignore.
      if (this.downAt === null) {
        this.downAt = now;
        this.polluted = false;
      }
      return;
    }
    // Any other key breaks the double-tap sequence and pollutes a hold
    // that's currently in progress (so a chord doesn't read as a tap).
    if (this.downAt !== null) this.polluted = true;
    this.lastTapEnd = Number.NEGATIVE_INFINITY;
  }

  /** Feed every keyup. Returns true exactly when this release completes a
   *  double tap of the tracked modifier. */
  onKeyUp(e: KeyEventLike, now: number): boolean {
    if (e.key !== this.key) return false;
    const downAt = this.downAt;
    this.downAt = null;
    const polluted = this.polluted;
    this.polluted = false;
    if (downAt === null) return false;

    const cleanTap = !polluted && now - downAt <= this.tapMaxMs;
    if (!cleanTap) {
      // A long hold or a chord — not a tap; drop any armed first tap.
      this.lastTapEnd = Number.NEGATIVE_INFINITY;
      return false;
    }
    if (now - this.lastTapEnd <= this.gapMaxMs) {
      // Second clean tap within the window → fire and consume, so a third
      // tap has to start a fresh pair rather than chaining.
      this.lastTapEnd = Number.NEGATIVE_INFINITY;
      return true;
    }
    // First clean tap — arm the window for a potential second.
    this.lastTapEnd = now;
    return false;
  }

  /** Forget any armed tap / in-flight hold (e.g. on window blur). */
  reset(): void {
    this.downAt = null;
    this.polluted = false;
    this.lastTapEnd = Number.NEGATIVE_INFINITY;
  }
}
